import { ResultsState, ResultsType } from 'enums'

import { type Result, ResultRequestType } from 'services/results/types'
import { isAdditionalInfoFormEnabled } from 'utils/env'

export const DEFAULT_PAGE_SIZE = 10
export const FIRST_PAGE = 1
export const RESULT_STATUS_REFETCH_INTERVAL_MS = 5000

/**
 * These statuses settle server-side without any user action, so views showing them poll until
 * the request reaches a state the reviewer can act on.
 */
export const shouldPollResultStatus = (status?: ResultsState): boolean =>
  status === ResultsState.Preparing || status === ResultsState.Processing

export const isDataAccessRequest = (exportRequest: Result): boolean =>
  exportRequest.requestType === ResultRequestType.DataAccessRequest ||
  (exportRequest.dataAccessRequest !== null && exportRequest.dataAccessRequest !== undefined)

/**
 * Retry re-runs the export approval, which has no equivalent for a data access request: its failure
 * is the participant file preparation, and re-preparing it is not supported yet.
 */
export const isRetrySupported = (exportRequest: Result): boolean => !isDataAccessRequest(exportRequest)

/**
 * A DAR is "external" when it originates from an external-organisation federated share,
 * i.e. the source organisation differs from the requester's organisation. Both ids must
 * be present; otherwise the request is treated as same-org and renders as today.
 */
export const isExternalDataAccessRequest = (exportRequest: Result): boolean => {
  const dar = exportRequest.dataAccessRequest
  if (!dar) return false

  const { sourceOrganisationId, requesterOrganisationId } = dar

  return !!sourceOrganisationId && !!requesterOrganisationId && sourceOrganisationId !== requesterOrganisationId
}

const getNameFromPrefix = (exportRequest: Result): string => {
  const prefix = exportRequest.beforeCheckLocation.prefix
  const name = prefix.substring(prefix.lastIndexOf('/') + 1)
  return name.length ? name : '--'
}

export const getRequestTypeDisplay = (requestType?: ResultRequestType): string => {
  switch (requestType) {
    case ResultRequestType.ExportRequest:
      return 'Export Request'
    case ResultRequestType.AppealRequest:
      return 'Appeal'
    case ResultRequestType.Attestation:
      return isAdditionalInfoFormEnabled() ? 'Additional Information Request' : 'Attestation'
    case ResultRequestType.DataAccessRequest:
      return 'Data Access'
    default:
      return '-'
  }
}

export const EXPORT_ONLY_REQUEST_TYPES: ResultRequestType[] = [
  ResultRequestType.ExportRequest,
  ResultRequestType.AppealRequest,
  ResultRequestType.Attestation
]

export const EXPORT_ONLY_REQUEST_TYPES_QUERY = EXPORT_ONLY_REQUEST_TYPES.join(',')

// Labels are derived from getRequestTypeDisplay so the filter options always read
// the same as the "Request type" column and the CSV export.
export const getExportRequestTypeFilterOptions = (): { value: ResultRequestType; label: string }[] =>
  EXPORT_ONLY_REQUEST_TYPES.map(requestType => ({ value: requestType, label: getRequestTypeDisplay(requestType) }))

export const getExportRequestName = (exportRequest: Result | undefined): string => {
  if (!exportRequest) return '--'

  if (isDataAccessRequest(exportRequest)) {
    const datasetName = exportRequest.dataAccessRequest?.datasetName?.trim()
    if (datasetName) return datasetName
    if (exportRequest.name && exportRequest.name.length) return exportRequest.name
    return getNameFromPrefix(exportRequest)
  }

  if (exportRequest.name && exportRequest.name.length) return exportRequest.name
  return getNameFromPrefix(exportRequest)
}

export const getCsvName = (resultType: ResultsType): string => {
  return `results_report_${resultType}.csv`
}
